import React, { useCallback } from 'react';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';
import { Modal } from 'antd';
import { createGlobalStyle } from 'styled-components';

import {
  EmbeddedBuilderEntity,
  EmbeddedBuilderMode,
  IBuilderSaveResult
} from 'components/EmbeddedBuilder';
import { DARK_MODAL_CLASS } from 'components/theme/DarkPageScope/constants';
import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import OrbitBuilderHost, { IOrbitBuilderRequest } from './OrbitBuilderHost';
import { BuilderRegistry } from './builderRegistry';

export const ORBIT_BUILDER_MODAL_CLASS = 'orbit-builder-modal';

/* The builder draws its own section cards and footer straight on the scrim (Figma frame 6), so the
   modal contributes no surface of its own. */
const OrbitBuilderModalStyles = createGlobalStyle`
  .${DARK_MODAL_CLASS}.${ORBIT_BUILDER_MODAL_CLASS} > .ant-modal > .ant-modal-content {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  /* The dark modal skin caps the body and paints it as one card; the builder brings its own cards and
     may use the frame height. Child combinators: a dialog the builder opens keeps its own surface. */
  .${DARK_MODAL_CLASS}.${ORBIT_BUILDER_MODAL_CLASS} > .ant-modal > .ant-modal-content > .ant-modal-body {
    max-height: calc(100vh - 72px);
    padding: 0;
    overflow: auto;
    border-radius: 8px;
    background: #05090f;
    ${intelligenceScrollbar('y', { pad: true })}
  }

  /* Every inner scroller too: tables, dropdown lists re-homed into the builder, rich-text bodies and
     the audience criteria panel. Axis x adds no gutter, so no inner container's layout changes. */
  .${DARK_MODAL_CLASS}.${ORBIT_BUILDER_MODAL_CLASS} .ant-modal-body * {
    ${intelligenceScrollbar('x')}
  }

  .${ORBIT_BUILDER_MODAL_CLASS} .ant-modal-close {
    color: #aebbd0;
  }
`;

const MASK_STYLE: React.CSSProperties = {
  background: 'rgba(3, 6, 12, 0.72)',
  backdropFilter: 'blur(7.5px)'
};

export interface IOrbitBuilderModalProps extends IOrbitBuilderRequest {
  registry?: BuilderRegistry;
}

/** Resolves with the save result, or `null` when the user leaves without saving. */
const OrbitBuilderModal = NiceModal.create((props: IOrbitBuilderModalProps) => {
  const modal = useModal();
  const modalProps = antdModalV5(modal);

  const close = useCallback(
    (result: IBuilderSaveResult | null) => {
      modal.resolve(result);
      modal.hide();
    },
    [modal]
  );

  return (
    <Modal
      {...modalProps}
      wrapClassName={`${DARK_MODAL_CLASS} ${ORBIT_BUILDER_MODAL_CLASS}`}
      width="calc(100vw - 234px)"
      style={{ top: 36, paddingBottom: 36 }}
      maskStyle={MASK_STYLE}
      footer={null}
      maskClosable={false}
      destroyOnClose
      onCancel={event => {
        // Esc inside a dialog the builder opened would otherwise close the builder as well.
        const wrap = (event?.target as HTMLElement | undefined)?.closest?.('.ant-modal-wrap');
        if (wrap && !wrap.classList.contains(ORBIT_BUILDER_MODAL_CLASS)) return;
        close(null);
      }}
    >
      <OrbitBuilderModalStyles />
      <OrbitBuilderHost {...props} onSaved={close} onCancel={() => close(null)} />
    </Modal>
  );
});

export const openOrbitBuilder = (request: IOrbitBuilderModalProps) =>
  NiceModal.show(OrbitBuilderModal, request) as Promise<IBuilderSaveResult | null>;

export interface IOpenBuilderOptions {
  module: EmbeddedBuilderEntity;
  mode: EmbeddedBuilderMode;
  /** Edit mode: the record to load. */
  recordId?: number;
  /** Create mode: the values to start from; edit mode: applied over the loaded record. */
  initialValues?: Record<string, unknown>;
  requestId?: number;
  initialStep?: string;
  /** Audience: custom-attribute keys that render as LOCKED FILTER rows. */
  lockedCustomAttributes?: string[];
  onSaved?: (result: IBuilderSaveResult) => void;
  onCancel?: () => void;
}

/** Opens the dark builder modal. Resolves with the save result, or `null` when dismissed. */
export const openBuilder = async ({
  module,
  mode,
  recordId,
  initialValues,
  requestId,
  initialStep,
  lockedCustomAttributes,
  onSaved,
  onCancel
}: IOpenBuilderOptions) => {
  const result = await openOrbitBuilder({
    entity: module,
    mode,
    entityId: recordId,
    prefill: initialValues,
    requestId,
    initialStep,
    lockedCustomAttributes
  });
  if (result) {
    onSaved?.(result);
  } else {
    onCancel?.();
  }
  return result;
};

export const useOrbitBuilder = () => ({ openBuilder });

// Development builds only: lets a browser script open any builder on any record (contrast sweeps).
if (process.env.NODE_ENV === 'development') {
  (window as unknown as { __orbitOpenBuilder?: typeof openBuilder }).__orbitOpenBuilder =
    openBuilder;
}

export default OrbitBuilderModal;
